/**
 * api.js — Fetch wrapper with automatic token injection and silent refresh.
 *
 * Rules:
 *  - Access token lives in memory only (passed in via getToken).
 *  - Every request includes credentials: 'include' so the httpOnly
 *    refresh_token cookie is sent automatically.
 *  - On 401, tries POST /auth/refresh once, then retries the original
 *    request with the new token. If refresh fails, calls onAuthFailure.
 */

import { API_BASE_URL as BASE_URL } from '../config';

let _getToken = () => null;
let _setToken = (_token) => {};
let _onAuthFailure = () => {};

/** Called once by AuthProvider to wire up token accessors. */
export function configureApi({ getToken, setToken, onAuthFailure }) {
  _getToken = getToken;
  _setToken = setToken;
  _onAuthFailure = onAuthFailure;
}

/**
 * Turn any caught error into a readable string.
 * "Failed to fetch" means the server is unreachable — say that clearly.
 */
function readableNetworkError(err) {
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    return 'Impossible de contacter le serveur. Vérifiez que le backend est démarré sur http://127.0.0.1:8000';
  }
  return err.message || 'Erreur réseau inconnue.';
}

/**
 * Extract the best error message from a non-OK response.
 * FastAPI detail can be a string or a Pydantic validation list.
 */
async function extractResponseError(res, fallback) {
  try {
    const body = await res.clone().json();
    if (typeof body.detail === 'string') return `${res.status} — ${body.detail}`;
    if (Array.isArray(body.detail)) {
      const msgs = body.detail.map((d) => {
        const field = d.loc ? d.loc.slice(-1)[0] : '';
        return field ? `${field}: ${d.msg}` : d.msg;
      });
      return `${res.status} — ${msgs.join(' · ')}`;
    }
    if (body.message) return `${res.status} — ${body.message}`;
  } catch {
    // body wasn't JSON
  }
  return `${res.status} — ${fallback}`;
}

/** Attempt a silent token refresh using the httpOnly cookie. */
async function refreshAccessToken() {
  let res;
  try {
    res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (err) {
    throw new Error(readableNetworkError(err));
  }
  if (!res.ok) throw new Error(await extractResponseError(res, 'Session expirée.'));
  const data = await res.json();
  return data.access_token;
}

/**
 * Main request helper. Mirrors the fetch() API.
 *
 * @param {string} path  — path relative to BASE_URL, e.g. '/api/v1/auth/me'
 * @param {RequestInit} options
 * @param {boolean} _isRetry — internal flag to prevent infinite retry loops
 */
export async function apiFetch(path, options = {}, _isRetry = false) {
  const token = _getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers,
    });
  } catch (err) {
    throw new Error(readableNetworkError(err));
  }

  // Silent refresh + retry on 401
  if (response.status === 401 && !_isRetry) {
    try {
      const newToken = await refreshAccessToken();
      _setToken(newToken);
      return apiFetch(path, options, true);
    } catch {
      _onAuthFailure();
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
  }

  return response;
}
